import React, { useState, useEffect, useMemo } from "react"
import { Col, Row, Spinner, Table } from "reactstrap"
import { Link } from "react-router-dom"
import api from "API/Api"
import { usePermissions } from "API/PermissionContext"

const PaymentLedger = () => {
  const { canExport, canApprove, MODULE } = usePermissions()
  const MID = MODULE.PAYMENTS

  const [loading, setLoading] = useState(true)
  const [entries, setEntries] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)

  // Filters
  const [filterType, setFilterType] = useState("")
  const [filterStatus, setFilterStatus] = useState("")
  const [filterMode, setFilterMode] = useState("")
  const [filterSearch, setFilterSearch] = useState("")
  const [filterFrom, setFilterFrom] = useState("")
  const [filterTo, setFilterTo] = useState("")

  // Detail view
  const [selectedEntry, setSelectedEntry] = useState(null)
  const [deductions, setDeductions] = useState([])
  const [showDetail, setShowDetail] = useState(false)

  useEffect(() => { loadLedger() }, [page, filterType, filterStatus, filterMode, filterFrom, filterTo])

  const loadLedger = async () => {
    setLoading(true)
    try {
      let url = `payment-ledger?per_page=100&page=${page}`
      if (filterType) url += `&payee_type=${filterType}`
      if (filterStatus) url += `&payment_status=${filterStatus}`
      if (filterMode) url += `&payment_mode=${filterMode}`
      if (filterFrom) url += `&from=${filterFrom}`
      if (filterTo) url += `&to=${filterTo}`
      if (filterSearch) url += `&search=${filterSearch}`

      const resp = await api.get(url)
      if (resp.data.success) {
        const d = resp.data.data
        setEntries(d?.data || d || [])
        setTotal(d?.total || 0)
      }
    } catch (e) { console.error(e) }
    setLoading(false)
  }

  const searchLedger = () => { setPage(1); loadLedger() }

  const viewDetail = async (entry) => {
    setSelectedEntry(entry)
    setShowDetail(true)
    try {
      const resp = await api.get(`payment-ledger/${entry.id}`)
      if (resp.data.success) {
        setDeductions(resp.data.data.deductions || [])
      }
    } catch (e) { console.error(e) }
  }

  const exportCSV = () => {
    if (!entries.length) return
    const h = ["S.No","Type","Name","Mobile","Period","Qty","Gross","Incentive","Loan Ded","Cash Adv Ded","Material Ded","Recovery Ded","Total Ded","Net Payable","Mode","Status","Payment Ref"]
    let csv = h.join(",") + "\n"
    entries.forEach((e, i) => {
      csv += [i+1, e.payee_type, `"${e.payee_name}"`, e.payee_mobile||"", `${e.period_from} to ${e.period_to}`, e.total_qty, e.gross_amount, e.incentive_amount, e.loan_deduction, e.cash_advance_deduction, e.material_deduction, e.recovery_deduction, e.total_deductions, e.net_payable, e.payment_mode, e.payment_status, e.payment_ref||""].join(",") + "\n"
    })
    const blob = new Blob([csv], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a"); a.href = url
    a.download = "payment_ledger_" + new Date().toISOString().slice(0,10) + ".csv"
    a.click(); window.URL.revokeObjectURL(url)
  }

  const statusBadge = (s) => {
    const m = { pending:"bg-secondary", approved:"bg-info", processing:"bg-primary", paid:"bg-success", failed:"bg-danger", held:"bg-warning text-dark", skipped:"bg-light text-dark" }
    return <span className={`badge ${m[s]||"bg-secondary"}`} style={{fontSize:"10px"}}>{(s||"").toUpperCase()}</span>
  }

  const summary = useMemo(() => {
    if (!entries.length) return null
    return {
      totalGross: entries.reduce((s,e) => s + parseFloat(e.gross_amount||0), 0),
      totalDed: entries.reduce((s,e) => s + parseFloat(e.total_deductions||0), 0),
      totalNet: entries.reduce((s,e) => s + parseFloat(e.net_payable||0), 0),
      paidCount: entries.filter(e => e.payment_status === "paid").length,
    }
  }, [entries])

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={6}><h4><i className="fas fa-book me-2" style={{color:"#6366f1"}}></i>Payment Ledger <span style={{fontSize:"13px",color:"#6c757d"}}>({total} records)</span></h4></Col>
          <Col md={6} style={{textAlign:"right"}}>
            <button className="btn btn-success btn-sm me-2" onClick={exportCSV} disabled={!entries.length}><i className="fas fa-file-csv me-1"></i>Export CSV</button>
            <Link className="dashBtn" to="/payment-dashboard"><i className="fas fa-chevron-left"></i> <b>Dashboard</b></Link>
          </Col>
        </Row>
      </div>
      <div className="dashContent">

        {/* Summary bar */}
        {summary && (
          <div style={{display:"flex",gap:"24px",marginBottom:"16px",flexWrap:"wrap"}}>
            <span style={{fontSize:"12px"}}><i className="fas fa-money-bill-wave me-1" style={{color:"#0284c7"}}></i>Gross: <strong>₹{summary.totalGross.toLocaleString("en-IN",{minimumFractionDigits:2})}</strong></span>
            <span style={{fontSize:"12px"}}><i className="fas fa-minus-circle me-1" style={{color:"#dc2626"}}></i>Deductions: <strong>₹{summary.totalDed.toLocaleString("en-IN",{minimumFractionDigits:2})}</strong></span>
            <span style={{fontSize:"12px"}}><i className="fas fa-check-circle me-1" style={{color:"#059669"}}></i>Net: <strong>₹{summary.totalNet.toLocaleString("en-IN",{minimumFractionDigits:2})}</strong></span>
            <span style={{fontSize:"12px"}}><i className="fas fa-check-double me-1" style={{color:"#22c55e"}}></i>Paid: <strong>{summary.paidCount}</strong></span>
          </div>
        )}

        {/* Filters */}
        <Row className="mb-3" style={{background:"#f8fafc",padding:"12px",borderRadius:"8px"}}>
          <Col md={2}><input type="text" className="form-control form-control-sm" placeholder="Search name..." value={filterSearch} onChange={e=>setFilterSearch(e.target.value)} onKeyPress={e=>{if(e.key==="Enter")searchLedger()}} /></Col>
          <Col md={2}>
            <select className="form-control form-control-sm" value={filterType} onChange={e=>setFilterType(e.target.value)}>
              <option value="">All Types</option><option value="producer">Producer</option><option value="vendor">Vendor</option>
            </select>
          </Col>
          <Col md={2}>
            <select className="form-control form-control-sm" value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
              <option value="">All Status</option><option value="pending">Pending</option><option value="approved">Approved</option><option value="processing">Processing</option><option value="paid">Paid</option><option value="held">Held</option><option value="failed">Failed</option><option value="skipped">Skipped</option>
            </select>
          </Col>
          <Col md={2}>
            <select className="form-control form-control-sm" value={filterMode} onChange={e=>setFilterMode(e.target.value)}>
              <option value="">All Modes</option><option value="cash">Cash</option><option value="bank">Bank</option><option value="upi">UPI</option>
            </select>
          </Col>
          <Col md={2}><input type="date" className="form-control form-control-sm" value={filterFrom} onChange={e=>setFilterFrom(e.target.value)} placeholder="From" /></Col>
          <Col md={2}><input type="date" className="form-control form-control-sm" value={filterTo} onChange={e=>setFilterTo(e.target.value)} placeholder="To" /></Col>
        </Row>

        {/* Table */}
        <div className="table-responsive" style={{background:"#fff",borderRadius:"8px"}}>
          <Table className="table mb-0" style={{fontSize:"12px"}}>
            <thead>
              <tr style={{background:"#f8fafc",fontSize:"10px",color:"#64748b",textTransform:"uppercase",letterSpacing:"0.5px"}}>
                <th>#</th><th>Type</th><th>Name</th><th>Period</th><th>Qty</th>
                <th>Gross (₹)</th><th>Deductions (₹)</th><th>Net (₹)</th>
                <th>Mode</th><th>Status</th><th>Payment Ref</th><th>Date</th><th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={13} style={{textAlign:"center",padding:"30px"}}><Spinner size="sm" /></td></tr> :
              entries.length === 0 ? <tr><td colSpan={13} style={{textAlign:"center",padding:"30px",color:"#94a3b8"}}>No ledger entries found</td></tr> :
              entries.map((e, i) => (
                <tr key={e.id||i} style={{cursor:"pointer",borderBottom:"1px solid #f1f5f9"}} onClick={()=>viewDetail(e)}>
                  <td>{(page-1)*100+i+1}</td>
                  <td><span className={`badge ${e.payee_type==="producer"?"bg-primary":"bg-info"}`} style={{fontSize:"9px"}}>{(e.payee_type||"").slice(0,4).toUpperCase()}</span></td>
                  <td style={{fontWeight:"600",maxWidth:"150px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.payee_name}</td>
                  <td style={{whiteSpace:"nowrap",fontSize:"10px"}}>{e.period_from} → {e.period_to}</td>
                  <td>{e.total_qty}</td>
                  <td style={{color:"#0284c7"}}>₹{Number(e.gross_amount||0).toLocaleString("en-IN")}</td>
                  <td style={{color:"#dc2626"}}>₹{Number(e.total_deductions||0).toLocaleString("en-IN")}</td>
                  <td style={{fontWeight:"700",color:"#059669"}}>₹{Number(e.net_payable||0).toLocaleString("en-IN")}</td>
                  <td>{e.payment_mode==="cash"?"💵":"🏦"}</td>
                  <td>{statusBadge(e.payment_status)}</td>
                  <td style={{fontSize:"10px",color:"#64748b",maxWidth:"100px",overflow:"hidden",textOverflow:"ellipsis"}}>{e.payment_ref||"—"}</td>
                  <td style={{fontSize:"10px"}}>{e.payment_date||"—"}</td>
                  <td><i className="fas fa-chevron-right" style={{color:"#94a3b8",fontSize:"10px"}}></i></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {/* Pagination */}
        {total > 100 && (
          <div style={{display:"flex",justifyContent:"center",gap:"8px",marginTop:"16px"}}>
            <button className="btn btn-outline-primary btn-sm" disabled={page===1} onClick={()=>setPage(p=>p-1)}>← Prev</button>
            <span style={{padding:"6px 12px",fontSize:"12px",color:"#6c757d"}}>Page {page} of {Math.ceil(total/100)}</span>
            <button className="btn btn-outline-primary btn-sm" disabled={page>=Math.ceil(total/100)} onClick={()=>setPage(p=>p+1)}>Next →</button>
          </div>
        )}

        {/* Detail Modal */}
        {showDetail && selectedEntry && (
          <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.5)",zIndex:1050,display:"flex",justifyContent:"center",alignItems:"center"}} onClick={()=>setShowDetail(false)}>
            <div style={{background:"#fff",borderRadius:"16px",padding:"24px",maxWidth:"700px",width:"90%",maxHeight:"80vh",overflow:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.15)"}} onClick={e=>e.stopPropagation()}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"16px"}}>
                <h6 style={{margin:0,fontWeight:"700",color:"#334155"}}><i className="fas fa-user me-2"></i>{selectedEntry.payee_name}</h6>
                <button className="btn btn-sm btn-outline-secondary" onClick={()=>setShowDetail(false)}><i className="fas fa-times"></i></button>
              </div>

              <Row className="mb-3">
                <Col md={4}><div style={{fontSize:"11px",color:"#6c757d"}}>Type</div><div style={{fontWeight:"600"}}>{selectedEntry.payee_type?.toUpperCase()}</div></Col>
                <Col md={4}><div style={{fontSize:"11px",color:"#6c757d"}}>Period</div><div style={{fontWeight:"600"}}>{selectedEntry.period_from} → {selectedEntry.period_to}</div></Col>
                <Col md={4}><div style={{fontSize:"11px",color:"#6c757d"}}>Status</div>{statusBadge(selectedEntry.payment_status)}</Col>
              </Row>

              <div style={{background:"#f8fafc",borderRadius:"8px",padding:"12px",marginBottom:"16px"}}>
                <Row>
                  <Col md={3}><div style={{fontSize:"11px",color:"#6c757d"}}>Gross</div><div style={{fontWeight:"700",color:"#0284c7"}}>₹{Number(selectedEntry.gross_amount||0).toLocaleString("en-IN")}</div></Col>
                  <Col md={3}><div style={{fontSize:"11px",color:"#6c757d"}}>Incentive</div><div style={{fontWeight:"600",color:"#059669"}}>₹{Number(selectedEntry.incentive_amount||0).toLocaleString("en-IN")}</div></Col>
                  <Col md={3}><div style={{fontSize:"11px",color:"#6c757d"}}>Deductions</div><div style={{fontWeight:"600",color:"#dc2626"}}>₹{Number(selectedEntry.total_deductions||0).toLocaleString("en-IN")}</div></Col>
                  <Col md={3}><div style={{fontSize:"11px",color:"#6c757d"}}>Net Payable</div><div style={{fontWeight:"700",fontSize:"18px",color:"#059669"}}>₹{Number(selectedEntry.net_payable||0).toLocaleString("en-IN")}</div></Col>
                </Row>
              </div>

              {/* Deduction Breakdown */}
              {deductions.length > 0 && (
                <>
                  <h6 style={{fontSize:"13px",fontWeight:"600",color:"#334155",marginBottom:"8px"}}><i className="fas fa-list me-1"></i>Deduction Details</h6>
                  <Table className="table table-sm mb-3" style={{fontSize:"11px"}}>
                    <thead><tr style={{background:"#fef2f2",fontSize:"10px",color:"#dc2626"}}><th>Type</th><th>Source</th><th>Original</th><th>Deducted</th><th>Remaining</th></tr></thead>
                    <tbody>
                      {deductions.map((d,i) => (
                        <tr key={i}>
                          <td><span className="badge bg-outline-danger" style={{border:"1px solid #fca5a5",color:"#dc2626",background:"#fff1f2"}}>{d.deduction_type}</span></td>
                          <td style={{color:"#6c757d"}}>{d.source_table} #{d.source_id}</td>
                          <td>₹{Number(d.original_amount||0).toLocaleString("en-IN")}</td>
                          <td style={{fontWeight:"600",color:"#dc2626"}}>₹{Number(d.deducted_amount||0).toLocaleString("en-IN")}</td>
                          <td>₹{Number(d.remaining_balance||0).toLocaleString("en-IN")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              {/* Bank/Payment Info */}
              {selectedEntry.payment_mode !== "cash" && (
                <div style={{background:"#f0f9ff",borderRadius:"8px",padding:"12px",fontSize:"12px"}}>
                  <div style={{fontWeight:"600",color:"#0284c7",marginBottom:"4px"}}><i className="fas fa-university me-1"></i>Bank Details</div>
                  {selectedEntry.bank_account_name && <div>Name: <strong>{selectedEntry.bank_account_name}</strong></div>}
                  {selectedEntry.bank_account_number && <div>Account: <strong>{selectedEntry.bank_account_number}</strong></div>}
                  {selectedEntry.bank_ifsc && <div>IFSC: <strong>{selectedEntry.bank_ifsc}</strong></div>}
                  {selectedEntry.upi_id && <div>UPI: <strong>{selectedEntry.upi_id}</strong></div>}
                  {selectedEntry.payment_ref && <div>Payment Ref: <strong>{selectedEntry.payment_ref}</strong></div>}
                </div>
              )}

              {selectedEntry.held_reason && (
                <div style={{background:"#fefce8",borderRadius:"8px",padding:"12px",marginTop:"8px",fontSize:"12px",color:"#a16207"}}>
                  <i className="fas fa-exclamation-triangle me-1"></i><strong>Held:</strong> {selectedEntry.held_reason}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PaymentLedger
